/**
 * FactTable — dense fact list rendered as a 2-column key/value table
 * with `lastVerified` badges.  Picks up resolved values from the article's
 * FactsRegistry via the fact ids declared in the WidgetPlan.
 *
 * Mobile: collapses to single column.  Always visible (no truncation).
 */

import type { LangCode } from '../architecture';
import type { ResolvedFact } from '../facts';

interface FactTableRow {
  /** Display label for the row. */
  label: string;
  /** Optional one-liner shown smaller under the label. */
  note?: string;
  /** Fact id to resolve via the registry. */
  factId: string;
}

interface FactTableProps {
  /** Authored rows from WidgetPlan.props.rows */
  rows: FactTableRow[];
  /** Resolved facts (kept positional with rows). */
  facts: Record<string, ResolvedFact>;
  lang: LangCode;
  caption?: string;
}

export function FactTable({ rows, facts, lang, caption }: FactTableProps) {
  return (
    <figure className="my-6 sm:my-8 rounded-lg border border-ink-100 bg-paper-2">
      {caption && (
        <figcaption className="border-b border-ink-100 px-4 py-2 font-mono text-[11px] uppercase tracking-wider text-ink-500">
          {caption}
        </figcaption>
      )}

      <dl className="divide-y divide-ink-100">
        {rows.map((r) => {
          const f = facts[r.factId];
          return (
            <div
              key={r.factId}
              className="grid grid-cols-1 gap-1 px-4 py-3 sm:grid-cols-[1fr_auto] sm:items-baseline sm:gap-4"
            >
              <dt className="min-w-0">
                <span className="block text-sm font-medium text-ink-800 sm:text-base">
                  {r.label}
                </span>
                {r.note && (
                  <span className="mt-0.5 block text-xs text-ink-500">{r.note}</span>
                )}
              </dt>
              <dd className="flex flex-col items-start gap-0.5 sm:items-end">
                <span className="font-display text-base font-medium italic tabular-nums text-terra-700 sm:text-lg">
                  {f ? f.formatted : '—'}
                </span>
                {f?.lastVerified && (
                  <span className="font-mono text-[10px] uppercase tracking-wider text-ink-400">
                    {lang === 'ru' ? 'пров.' : 'verif.'} · {f.lastVerified}
                  </span>
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    </figure>
  );
}
